import React from 'react';
import './CitationButton.scss';
import type { MessageReference } from '../../../../types/chat-message';
import { Dropdown } from '../../../../components/Dropdown';

interface CitationButtonProps {
  references: MessageReference[];
}

/** Số favicon xếp chồng trên chip "Nguồn" */
const MAX_STACKED_FAVICONS = 3;

// Grounding Google trả link redirect (vertexaisearch.cloud.google/...) — domain thật nằm ở title
const REDIRECT_HOST_RE = /(^|\.)vertexaisearch\.cloud\.google(\.com)?$/i;
const DOMAIN_LIKE_RE = /^(?:www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+$/i;

const getHostname = (reference: MessageReference): string | null => {
  try {
    const hostname = new URL(reference.uri).hostname;
    if (!REDIRECT_HOST_RE.test(hostname)) return hostname;
    const title = (reference.title || '').trim();
    return DOMAIN_LIKE_RE.test(title) ? title : null;
  } catch {
    return null;
  }
};

/**
 * youtube.com → "Youtube", vanmien.gov.vn → "Vanmien.gov" (bỏ www + đuôi cuối, viết hoa chữ đầu)
 */
const getSource = (reference: MessageReference): { name: string; favicon: string | null } => {
  const hostname = getHostname(reference);
  if (!hostname) return { name: 'Web', favicon: null };
  const parts = hostname.replace(/^www\./, '').split('.');
  const base = parts.length > 1 ? parts.slice(0, -1).join('.') : parts[0];
  const name = base.charAt(0).toUpperCase() + base.slice(1);
  const favicon = `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
  return { name, favicon };
};

const Favicon: React.FC<{ src: string | null; className: string }> = ({ src, className }) =>
  src ? (
    <img
      src={src}
      alt=""
      className={className}
      onError={(e) => {
        e.currentTarget.style.visibility = 'hidden';
      }}
    />
  ) : (
    <span className={className} aria-hidden />
  );

const CitationButton: React.FC<CitationButtonProps> = ({ references }) => {
  if (!references || references.length === 0) {
    return null;
  }

  const sources = references.map(getSource);
  const stacked = sources.slice(0, MAX_STACKED_FAVICONS);

  const items = references.map((reference: MessageReference, index: number) => {
    const { name, favicon } = sources[index];
    return {
      key: String(index),
      label: (
        <div className="citation-dropdown-item-content">
          <div className="citation-dropdown-source">
            <Favicon src={favicon} className="citation-dropdown-favicon" />
            <span className="citation-dropdown-domain">{name}</span>
          </div>
          <div className="citation-dropdown-title">{reference.title || reference.uri}</div>
        </div>
      ),
      onClick: () => window.open(reference.uri, '_blank', 'noopener,noreferrer'),
    };
  });

  return (
    <Dropdown
      items={items}
      placement="top"
      overlayClassName="citation-dropdown-overlay"
      trigger={['hover']}
      // Render ra body: thoát overflow của bubble để antd tự đẩy popup vào trong mép panel,
      // và nằm trên nút nổi bên phải
      getPopupContainer={() => document.body}
      overlayStyle={{ zIndex: 100000 }}
    >
      <button
        className="citation-button"
        onClick={(e) => {
          e.preventDefault();
        }}
        aria-label={`Xem ${references.length} nguồn`}
        type="button"
      >
        <span className="citation-button-favicons" aria-hidden>
          {stacked.map((source, index) => (
            <Favicon
              key={`${source.name}-${index}`}
              src={source.favicon}
              className="citation-button-favicon"
            />
          ))}
        </span>
        <span className="citation-button-label">Nguồn</span>
        {/* Figma: "Nguồn +X" với X = tổng số nguồn (1 link → "Nguồn +1") */}
        <span className="citation-count">+{references.length}</span>
      </button>
    </Dropdown>
  );
};

export default CitationButton;
